import {
  type InterpolatorConfig,
  animated,
  useSpring,
} from "@react-spring/web";
import { useCallback, useEffect, useRef } from "react";
import { cn } from "../../../util/classNameConstructor";

interface HorizontalScrollTeaserProps
  extends React.HTMLAttributes<HTMLDivElement> {
  out?: {
    config?: InterpolatorConfig;
    delay?: number;
    left?: number;
  };
  back?: {
    config?: InterpolatorConfig;
    delay?: number;
  };
  bounce?: boolean;
  classNames?: {
    wrapper?: string;
    container?: string;
  };
}
const HorizontalScrollTeaser = ({
  className,
  classNames,
  out,
  back,
  bounce,
  children,
}: HorizontalScrollTeaserProps) => {
  const [scrollProps, setScroll] = useSpring(() => ({ left: 0 }));
  const ref = useRef<HTMLDivElement>(null);

  const timeoutId = useRef<ReturnType<typeof setTimeout>>();
  const clear = useCallback(() => {
    if (scrollProps.left.get() === 0) return;
    setScroll({ left: 0, config: { tension: 300, friction: 40 } });
    clearTimeout(timeoutId.current);
  }, []);

  useEffect(() => {
    const scrollTeaser = () => {
      timeoutId.current = setTimeout(
        () =>
          setScroll({
            left: out?.left || -50,
            config: out?.config || { tension: 100, friction: 30 },
          }),
        out?.delay ?? 500,
      );

      timeoutId.current = setTimeout(
        () =>
          setScroll({
            left: 0,
            config:
              back?.config ||
              (bounce
                ? { tension: 200, friction: 15 }
                : { tension: 300, friction: 20 }),
          }),
        back?.delay ?? 1500,
      );
    };

    scrollTeaser();

    ref?.current?.addEventListener("scroll", clear);
    return () => ref?.current?.removeEventListener("scroll", clear);
  }, [setScroll]);

  return (
    <div
      className={cn(
        "h-full w-full overflow-x-auto overflow-y-hidden p-4",
        className,
        classNames?.wrapper,
      )}
      ref={ref}
    >
      <animated.div
        className={cn(
          "inline-flex h-full gap-4 after:-ml-4 after:w-4 after:shrink-0",
          classNames?.container,
        )}
        style={{
          translateX: scrollProps.left.to((e) => (bounce ? -Math.abs(e) : e)),
        }}
      >
        {children}
      </animated.div>
    </div>
  );
};

export default HorizontalScrollTeaser;
